import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';

import {Subscription} from 'rxjs';
import {LoaderService} from '../../../../services/loader.service';
import {Breadcrumb} from '../../../../types/Breadcrumb';
import {RoutesEnums, RoutesNamesEnum} from '../../../../enums/RoutesEnums';
import {ToolsService} from '../../../../services/tools.service';
import {HttpResponse} from '@angular/common/http';
import {MetaTag} from '../../../../types/MetaTag';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {WpService} from '../../../../services/wp.service';
import {LinkService} from '../../../../services/link.service';
import {WPPostNative} from '../../../../types/WPPostNative';
import {finalize} from 'rxjs/operators';
import {NativesUrlResolverService} from '../../services/natives-url-resolver.service';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchService} from '../../../../services/search/search.service';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {StaticNativesService} from '../../../../services/static-natives.service';
import {StaticSidebarPuffsService} from '../../../../services/static-sidebar-puffs.service';

@Component({
  selector: 'app-natives',
  templateUrl: './natives.component.html',
  styleUrls: ['./natives.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NativesComponent implements OnInit, OnDestroy {
  public breadcrumbs: Breadcrumb[] = [];
  public posts: WPPostNative[] = [];
  public baseUrl: string[] = ['/', RoutesEnums.NATIVES];
  public total: number;
  public totalPages: number;
  public pageTitle;
  public pageSize: number;
  public loadedCpruTypes: boolean = false;
  public cpruTypes: number[] = [];
  public pageType: DLPageTypeEnum = DLPageTypeEnum.ARTICLE_PAGE;
  private pageTitleMeta: string;
  private subscriptions: Subscription[] = [];

  constructor(
    public urlResolver: NativesUrlResolverService,
    private nativesService: StaticNativesService,
    private loaderService: LoaderService,
    private route: ActivatedRoute,
    private router: Router,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    private metaService: SeoMetaService,
    private wpService: WpService,
    private linkService: LinkService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private appConfigService: AppConfigService,
    private sidebarPuffsService: StaticSidebarPuffsService,
    private searchService: SearchService,
    private _change: ChangeDetectorRef,
    private reloadService: SearchReloadService,
  ) {
    this.pageSize = this.appConfigService.appConfig.articlesPageSize;
  }

  ngOnInit() {
    this.searchService.softResetState();
    this.onRouteUpdate();
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onRouteUpdate();
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public trackByMethod(i: number, el: WPPostNative) {
    return el.id;
  }

  private onRouteUpdate(): void {
    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    const isRedirected: boolean = this.isRedirectedAfterInitUrlResolverAndUpdateState();
    this.loadPosts(isRedirected);
  }

  private isRedirectedAfterInitUrlResolverAndUpdateState(): boolean {
    const isRedirected: boolean = this.urlResolver.cleanUpUrl(this.route.snapshot);
    this.urlResolver.resetState();
    const isRedirectedBadParams = this.urlResolver.processRouteQueryParams(this.route.snapshot.queryParams);
    this.urlResolver.processRouteParams(this.route.snapshot.params);
    return isRedirected || isRedirectedBadParams;
  }

  private loadPosts(isRedirected: boolean): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.subscriptions.push(
      this.nativesService
        .GetNativesLatestResponse(this.urlResolver.page)
        .pipe(
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          }),
        )
        .subscribe(
          (result) => this.processResults(result, isRedirected),
          (error) => {
            console.error(error);
            this.urlResolver.conditionalRedirectBadPage();
            this.onDataLoadingFinish();
          },
        ),
    );
  }

  private buildBreadcrumbs() {
    this.breadcrumbs = [];
    this.breadcrumbs.push({
      routeParams: ['/', RoutesEnums.NATIVES],
      routeQueryParams: null,
      anchor: RoutesNamesEnum.NATIVES,
    });
  }

  private processResults(response: HttpResponse<WPPostNative[]> & {totalPages: number}, isRedirected: boolean): void {
    const posts: WPPostNative[] =
      response != null && response.body != null ? response.body.filter((item) => this.filterNativePosts(item)) : null;
    this.posts = posts;
    this.totalPages = parseInt(response.totalPages.toString(), 10);
    this.total = this.totalPages * this.pageSize;
    this.urlResolver.setTotal(this.total);
    const isRedirected404: boolean = this.urlResolver.conditionalRedirectEmptyResultsPage();
    this.buildBreadcrumbs();
    this.scrollToPageTop();
    this.updateMetaTags();
    this.updateCpruTypes();
    this.sidebarPuffsService.reloadCombinedPuffs(RoutesEnums.NATIVES, null, this.cpruTypes, null, null, null);
    if (isRedirected === false && isRedirected404 === false) {
      const customGa4Params: DataLayerParamsPageViewGA4 = this.wpService.createDataLayerGa4CustomParamsForItemsList(
        this.posts,
        DLPageTypeEnum.ARTICLE_PAGE,
        this.pageTitleMeta,
      );
      this.pageViewGa4Resolver.trackPageView(customGa4Params);
    }
    this.onDataLoadingFinish();
  }

  private filterNativePosts(post: WPPostNative): boolean {
    return !(post && post.acf && post.acf.isPreview === true);
  }

  private scrollToPageTop(): void {
    this.tools.scrollTo();
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    const suffix = ' - Vården.se';
    let url;
    let title;
    url = this.urlBuilder.CreateStaticLink(this.urlBuilder.CreateNativesRouterLink());
    title = 'Artiklar';
    this.pageTitle = title;
    const urlWithPage = url + (this.urlResolver.page > 1 ? `?page=${this.urlResolver.page}` : '');

    if (this.urlResolver.page > 1) {
      title += ` - sida ${this.urlResolver.page}`;
    }

    this.pageTitleMeta = title + suffix;

    metaTags.push({
      value: title + suffix,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: title + suffix,
      type: 'property',
      field: 'og:title',
    });

    metaTags.push({
      value: 'website',
      type: 'property',
      field: 'og:type',
    });

    metaTags.push({
      value: urlWithPage,
      type: 'property',
      field: 'og:url',
    });

    metaTags.push({
      value: 'Artiklar',
      type: 'name',
      field: 'description',
    });

    metaTags.push({
      value: 'Artiklar',
      type: 'property',
      field: 'og:description',
    });

    metaTags.push({
      value: this.appConfigService.appConfig.siteName,
      type: 'property',
      field: 'og:site_name',
    });

    this.metaService.updateMetaTags(metaTags);

    if (this.urlResolver.page > 1) {
      this.linkService.updateTag({
        rel: 'prev',
        href: `${url}?page=${this.urlResolver.page - 1}`,
      });
    }
    if (this.urlResolver.page < this.totalPages) {
      this.linkService.updateTag({
        rel: 'next',
        href: `${url}?page=${this.urlResolver.page + 1}`,
      });
    }
  }

  private onDataLoadingFinish(): void {
    this._change.markForCheck();
  }

  private updateCpruTypes() {
    this.cpruTypes = [];
    if (!this.tools.isNil(this.posts)) {
      this.posts.forEach((post) => {
        if (
          post.acf &&
          post.acf.mainCareProviderUnitType &&
          this.cpruTypes.indexOf(post.acf.mainCareProviderUnitType.term_id) === -1
        ) {
          this.cpruTypes.push(post.acf.mainCareProviderUnitType.term_id);
        }
      });
    }
    this.loadedCpruTypes = true;
  }
}
